import {useNavigate} from "react-router-dom";
import {Card} from "@components/ui/card.tsx";
import {useRuleUpdate} from "@hooks/rule.ts";
import React, {FC, useState} from "react";
import {isManagedRule, RuleStatus, RuleType} from "@types";
import {ToggleSwitch} from "@components/ui/toggle-switch.tsx";
import {CreateRuleButton} from "@pages/automate/components/create.tsx";

export type ListRulesProps = {
    ruleType: RuleType;
    rules: Array<{
        id: string;
        name: string;
        status: RuleStatus;
        rule: RuleType;
    }>
};

export const ListRules: FC<ListRulesProps> = React.memo(({rules, ruleType}) => {
    const managed = isManagedRule(ruleType);

    if (rules.length === 0) {
        if (!managed) {
            return (
                <div className="pt-8 flex flex-col space-y-10 items-center justify-center">
                    <p className="text-orange-200 text-center backdrop-blur-xl">You have not created any rules yet.</p>
                    <CreateRuleButton title={'Create your first rule now'} ruleType={ruleType}/>
                </div>
            );
        } else {
            return (
                <div className="pt-8 flex flex-col space-y-6 items-center justify-center">
                    <p className="text-orange-200 text-center backdrop-blur-xl">We have not trained any model for this,
                        yet.</p>
                    <p className="text-orange-500 text-center backdrop-blur-xl">Reach out to us if you are interested in
                        it</p>
                </div>
            );
        }
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rules?.map(r => (
                <Rule key={r.id} {...r} />
            ))}
            {!managed && (<CreateRuleButton title={'Add another Rule'} ruleType={ruleType}/>)}
        </div>
    );
});

const Rule = ({id, name, status}: { id: string; name: string, status: RuleStatus }) => {
    const navigate = useNavigate();
    const [, updatingRaw, , updateRuleRaw] = useRuleUpdate();
    const [ruleStatus, setRuleStatus] = useState<RuleStatus>(status);
    const [updating, setUpdating] = useState(false);

    const handleStatusChange = (newStatus: RuleStatus) => {
        if (newStatus !== ruleStatus) {
            setRuleStatus(newStatus);
            const abortController = new AbortController();
            setUpdating(true);

            const startTime = Date.now();
            updateRuleRaw(id, {status: newStatus}, abortController);

            setTimeout(() => {
                const elapsed = Date.now() - startTime;
                const remaining = Math.max(0, 1000 - elapsed);
                setTimeout(() => setUpdating(false), remaining);
            }, 0);
        }
    };

    return (
        <Card
            className="w-full flex items-center justify-between rounded-xl border border-orange-200/60 shadow-xl bg-gradient-to-br from-orange-100/20 via-orange-50/10 to-transparent backdrop-blur-md"
        >
            <div
                className={`w-full p-4 flex items-center gap-2 transition ${
                    updating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
                onClick={!updating ? () => navigate(`/automate/rules/${id}`) : undefined}
            >
        <span className="text-base font text-orange-300">
            {name}
        </span>
                {updating && <span className="animate-spin">⏳</span>}
            </div>

            <div className="p-4">
                <StatusButton
                    status={ruleStatus}
                    onChange={handleStatusChange}
                    disabled={updating}
                />
            </div>
        </Card>
    );
};


type StatusButtonProps = {
    status: RuleStatus;
    onChange: (status: RuleStatus) => void;
    disabled: boolean;
}

const StatusButton: FC<StatusButtonProps> = ({status, onChange, disabled}) => {
    const toggled = status === RuleStatus.ACTIVE || status === RuleStatus.ACTIVE_EXHAUSTED;
    return (
        <div className={" flex items-center space-x-2"}>
            <div className={"w-full"}>
                <ToggleSwitch
                    activeLabel={'Active'}
                    inactiveLabel={'Inactive'}
                    value={toggled}
                    disabled={disabled}
                    onChange={() => {
                        switch (status) {
                            case RuleStatus.ACTIVE:
                                onChange(RuleStatus.INACTIVE);
                                break;
                            case RuleStatus.ACTIVE_EXHAUSTED:
                                onChange(RuleStatus.INACTIVE_EXHAUSTED);
                                break;
                            case RuleStatus.INACTIVE:
                                onChange(RuleStatus.ACTIVE);
                                break;
                            case RuleStatus.INACTIVE_EXHAUSTED:
                                onChange(RuleStatus.ACTIVE_EXHAUSTED);
                                break
                        }
                    }}
                />
            </div>
        </div>
    );
}

